/**
 * Verified against the RFC 7636 Appendix B test vector:
 *
 *   verifier  = "dBjftJeZ4CVP-mB92K27uhbUJU1p1r_wW1gFWFOEjXk"
 *   SHA-256   = [19,211,30,150,26,216,236,47,22,177,12,76,152,46,8,118,
 *                168,120,173,109,124,68,69,102,238,24,146,74,203,112,251,195]
 *   challenge = "E9Melhoa2OwvFrEMTJguCHaoeK1t8URWbuGJSstw-cM"
 *
 * The vector exercises the full chain: ASCII encoding of the verifier,
 * SHA-256 over those bytes, then base64url without padding. A mismatch
 * points at base64url handling (padding or the +/ → -_ substitution),
 * since a wrong digest would fail far more loudly.
 * 
 * PKCE authorization code flow — how these helpers fit together.
 *
 * 1) Before the redirect (still in your app):
 *      const verifier  = generateCodeVerifier();
 *      const challenge = await computeS256Challenge(verifier);
 *      const state     = generateState();
 *    Persist `verifier` and `state` in sessionStorage. sessionStorage, not
 *    localStorage: the value must not survive the tab or leak to other tabs.
 *
 * 2) Build the authorization request and redirect the user agent:
 *      response_type=code
 *      client_id, redirect_uri, scope
 *      state=<state>
 *      code_challenge=<challenge>
 *      code_challenge_method=S256
 *    The verifier itself is never sent here — only its hash. That is the whole
 *    point: an attacker intercepting the authorization request learns nothing
 *    reusable.
 *
 * 3) The authorization server authenticates the user and stores the challenge
 *    alongside the issued authorization code.
 *
 * 4) Callback: redirect_uri?code=<code>&state=<state>.
 *    Compare the returned state against the persisted one BEFORE touching the
 *    code. Mismatch or missing value => abort, do not exchange. This is CSRF
 *    protection and is independent of PKCE.
 *
 * 5) Token request (POST, form-encoded, to the token endpoint):
 *      grant_type=authorization_code
 *      code=<code>
 *      redirect_uri=<same value as in step 2>
 *      client_id
 *      code_verifier=<verifier>
 *
 * 6) The server recomputes base64url(SHA-256(code_verifier)) and compares it to
 *    the challenge from step 2. Mismatch => invalid_grant, no tokens issued.
 *
 * 7) Clear `verifier` and `state` from storage regardless of outcome. Both are
 *    single-use.
 */

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateRandomString(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  return base64UrlEncode(bytes);
}

/** 32 Bytes → 43 characters base64url (RFC 7636: 43–128 characters are allowed) */
export function generateCodeVerifier(): string {
  return generateRandomString(32);
}

export async function computeS256Challenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

export function generateState(): string {
  return generateRandomString(24);
}
